/**
 * KinKen Kost Elite - Design Tokens
 * Centralized design system for colors, typography, and spacing
 * 
 * Usage: Import these tokens instead of hardcoding values
 * This makes theming and maintenance much easier
 */

// ===========================================
// COLOR PALETTE
// ===========================================

export const COLORS = {
  // Primary - Gold/Bronze (Luxury accent)
  primary: {
    DEFAULT: "#8E7C5E",
    light: "#C4B49A",
    dark: "#7A6A4E",
    foreground: "#FFFFFF",
  },
  
  // Secondary - Charcoal (Text, headers)
  secondary: {
    DEFAULT: "#1A1A1A",
    light: "#333333",
    foreground: "#F5F5F0",
  },
  
  // Accent - Off-white (Backgrounds)
  accent: {
    DEFAULT: "#F5F5F0",
    light: "#FAFAFA",
    dark: "#F0F0EA",
    foreground: "#1A1A1A",
  },
  
  // Neutral grays
  gray: {
    50: "#FAFAFA",
    100: "#F5F5F5",
    200: "#E5E5E5",
    300: "#D4D4D4",
    400: "#A3A3A3",
    500: "#737373",
    600: "#525252",
    700: "#404040",
    800: "#262626",
    900: "#171717",
  },
  
  // Semantic colors
  success: "#22C55E",
  error: "#EF4444",
  warning: "#F59E0B",
  info: "#3B82F6",
  
  // Base
  white: "#FFFFFF",
  black: "#000000",
  background: "#FFFFFF",
  foreground: "#1A1A1A",
} as const;

// ===========================================
// TYPOGRAPHY
// ===========================================

export const TYPOGRAPHY = {
  // Font families (matches tailwind.config.ts)
  fontFamily: {
    serif: "var(--font-playfair), serif", // Playfair Display - headings
    sans: "var(--font-lato), sans-serif", // Lato - body text
  },
  
  // Font sizes
  fontSize: {
    xs: "0.75rem",      // 12px
    sm: "0.875rem",     // 14px
    base: "1rem",       // 16px
    lg: "1.125rem",     // 18px
    xl: "1.25rem",      // 20px
    "2xl": "1.5rem",    // 24px
    "3xl": "1.875rem",  // 30px
    "4xl": "2.25rem",   // 36px
    "5xl": "3rem",      // 48px
    "6xl": "3.75rem",   // 60px
  },
  
  // Font weights
  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    bold: 700,
  },
  
  // Letter spacing
  letterSpacing: {
    tight: "-0.025em",
    normal: "0",
    wide: "0.05em",
    wider: "0.1em",
    widest: "0.2em",
    luxury: "0.25em", // For luxury uppercase labels
  },
  
  // Line heights
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  },
} as const;

// ===========================================
// SPACING
// ===========================================

export const SPACING = {
  section: {
    sm: "3rem",     // py-12
    md: "5rem",     // py-20
    lg: "8rem",     // py-32
  },
  container: {
    paddingX: {
      sm: "1.5rem", // px-6
      md: "3rem",   // px-12
    },
  },
} as const;

// ===========================================
// ANIMATIONS
// ===========================================

export const ANIMATION = {
  duration: {
    fast: 300,
    normal: 500,
    slow: 700,
    verySlow: 1000,
  },
  easing: {
    default: "cubic-bezier(0.4, 0, 0.2, 1)",
    easeIn: "cubic-bezier(0.4, 0, 1, 1)",
    easeOut: "cubic-bezier(0, 0, 0.2, 1)",
    easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
    luxury: "cubic-bezier(0.77, 0, 0.175, 1)",
  },
} as const;

// ===========================================
// BREAKPOINTS
// ===========================================

export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
} as const;

// ===========================================
// Z-INDEX
// ===========================================

export const Z_INDEX = {
  base: 0,
  dropdown: 10,
  sticky: 20,
  fixed: 30,
  overlay: 40,
  modal: 50,
  popover: 60,
  tooltip: 70,
  navbar: 80,
  menu: 90,
  max: 100,
} as const;

// ===========================================
// TYPE EXPORTS
// ===========================================

export type ColorKey = keyof typeof COLORS;
export type BreakpointKey = keyof typeof BREAKPOINTS;
